import { useState, useCallback, createContext, useContext } from "react";

const ConfirmContext = createContext(null);

export const ConfirmDialogProvider = ({ children }) => {
  const [state, setState] = useState(null);

  const confirm = useCallback(({ title, message, confirmText, confirmVariant }) => {
    return new Promise((resolve) => {
      setState({
        title: title || "Confirm",
        message,
        confirmText: confirmText || "OK",
        confirmVariant: confirmVariant || "default",
        resolve,
      });
    });
  }, []);

  const handleConfirm = () => {
    state?.resolve(true);
    setState(null);
  };

  const handleCancel = () => {
    state?.resolve(false);
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-lg p-6 max-w-sm mx-4 space-y-4">
            <h3 className="text-lg font-semibold">{state.title}</h3>
            <p className="text-sm text-muted-foreground whitespace-pre-line">{state.message}</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm rounded-md border hover:bg-muted"
                onClick={handleCancel}
                data-testid="confirm-dialog-cancel"
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 text-sm rounded-md ${
                  state.confirmVariant === "destructive"
                    ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    : "bg-primary text-primary-foreground hover:bg-primary/90"
                }`}
                onClick={handleConfirm}
                data-testid="confirm-dialog-confirm"
              >
                {state.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
};

export const useConfirm = () => {
  const confirm = useContext(ConfirmContext);
  if (!confirm) throw new Error("useConfirm must be used within ConfirmDialogProvider");
  return confirm;
};
